import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Result } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import {
  buildFlowLayout,
  buildLinks,
  buildMentalLayout,
  dependencyLinks,
  LINK_MARKERS,
  MapBox,
  MapLayout,
  MapNode,
  MapPartner,
  partnersOf,
  rootIdOf,
} from './mapLayout';


import { getBindEdges, getHierarchyAccounts } from '../../../../hooks/interoperability';
import {
  BindEdge,
  Connection,
  InviteReview,
  ScopeAccount,
} from '../../../../types/responses/Interoperability';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { partnerLogoUrl } from '../../../molecules/PartnerLogo';
import { directionOfInvite, STATUS_STYLE } from '../contractRules';

const Head = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px 8px 0 0;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #12263f;
`;

const Hint = styled.div`
  color: #8a9099;
  font-size: 11px;
  margin-top: 3px;
  max-width: 520px;
`;

const ViewSwitch = styled.div`
  display: flex;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  overflow: hidden;
  flex: none;
`;

const ViewButton = styled.button<{ active: boolean }>`
  padding: 6px 13px;
  border: none;
  border-left: 1px solid #e3e6ea;
  background: ${({ active, theme }) => (active ? theme.primary : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#4a5462')};
  font-weight: 500;
  font-size: 11.5px;
  cursor: pointer;

  &:first-child {
    border-left: none;
  }
`;

const Canvas = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-top: none;
  overflow: hidden;

  /*
    Altura de uma tabela de dez linhas, que e o tamanho das outras listagens do modulo.

    A janela saiu da conta da ALTURA de proposito. Era clamp(..., calc(100vh - 320px), ...), e
    aquele desconto cobria so o que vem acima do canvas: sobravam o cabecalho do proprio mapa e
    a legenda embaixo dele, entao o bloco passava do rodape e a barra de rolagem aparecia na
    PAGINA, nao aqui dentro. Errar o desconto para cima ou para baixo e facil, e o sintoma nao
    aparece na tela de quem escreveu.

    Fica px: 560px e o cabecalho mais dez linhas desta tabela. A janela volta so como teto, para
    tela baixa encolher em vez de empurrar a pagina.

    overflow: hidden, e nao auto: com viewBox o desenho ja se ajusta, e "auto" deixava a porta
    aberta para a barra voltar por arredondamento. O resize: vertical continua valendo — ele
    precisa de overflow diferente de visible, que hidden atende.
  */
  height: 560px;
  max-height: calc(100vh - 220px);
  min-height: 320px;
  resize: vertical;
`;

const Legend = styled.div`
  background: #fafbfc;
  border: 1px solid #e9ebee;
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #6b7280;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
`;

/**
 * Caixa e textos do no como styled SVG.
 *
 * A cor do no raiz e a do tenant, e atributo de SVG calculado em JS nao passa pelo
 * styled-components — o useTheme do repo devolve o NOME do tema, nao a paleta. Como
 * styled, o `theme` do ThemeProvider chega por prop e o white-label funciona.
 */
const NodeRect = styled.rect<{ root: boolean; dimmed: boolean }>`
  fill: ${({ root, theme }) => (root ? theme.primary : '#fff')};
  stroke: ${({ root, dimmed, theme }) =>
    (dimmed ? '#e9ebee' : root ? theme.primary : '#d9dce1')};
`;

const NodeTitle = styled.text<{ root: boolean; dimmed: boolean }>`
  fill: ${({ root, dimmed }) => (root ? '#fff' : dimmed ? '#9aa3af' : '#1f2430')};
  font-size: 11.5px;
  font-weight: 600;
`;

const NodeSubtitle = styled.text<{ root: boolean; dimmed: boolean }>`
  fill: ${({ root, dimmed }) =>
    (root ? 'rgba(255,255,255,.72)' : dimmed ? '#c6cbd3' : '#8a9099')};
  font-size: 10.5px;
`;

const Empty = styled.div`
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  color: #8a9099;
`;

type MapView = 'flow' | 'mental';

export type MapTabProps = {
  tenantId: string;
  connections: Connection[];

  /**
   * Parceiro isolado no desenho, vindo da URL. Fica fora do componente para o
   * link poder apontar direto para as ligações de um parceiro.
   */
  selectedPartnerId: string | null;
  onSelectPartner: (partnerTenantId: string | null) => void;

  /**
   * Abre o contrato do parceiro para edicao.
   *
   * Clicar na ligacao leva ao painel da aba Conexoes, que e onde o contrato se
   * altera — o mapa mostra, e nao edita.
   */
  onEditContract: (partnerTenantId: string) => void;

  /** Convites recebidos e enviados: viram parceiros pendentes no desenho. */
  receivedInvites: InviteReview[];
  sentInvites: InviteReview[];
};

/**
 * Aba Mapa de ligações (MV2-1719).
 *
 * Duas leituras da mesma coisa: Fluxo põe minhas contas de um lado e os
 * parceiros do outro; Mapa mental põe a matriz no centro. Nenhuma das duas
 * inventa dado — as ligações vêm dos vínculos, uma requisição só.
 *
 * Clicar num parceiro isola as ligações dele. É a única interação: o mapa
 * mostra, quem altera contrato é a aba Conexões.
 */
export const MapTab: React.FC<MapTabProps> = ({
  tenantId,
  connections,
  receivedInvites,
  sentInvites,
  selectedPartnerId,
  onSelectPartner,
  onEditContract,
}) => {
  const [view, setView] = useState<MapView>('flow');

  /**
   * Aliases cujo logo existe de verdade, confirmados por pre-carregamento.
   *
   * SVG nao tem a reserva declarativa que o <img> da lista tem, e imagem que falha
   * pode virar icone de quebrado dentro do desenho. Entao aqui a ordem se inverte:
   * carrega antes, desenha depois. Parceiro sem logo no repositorio white-label e o
   * caso comum, e o no simplesmente fica so com texto.
   */
  const [logoReady, setLogoReady] = useState<string[]>([]);

  const canvasRef = useRef<HTMLDivElement>(null);

  /** Deslocamento no inicio do gesto, para o arraste da raiz nao se acumular. */
  const draggedRef = useRef<Record<string, { dx: number; dy: number }>>({});

  /** Area util medida, para o desenho ocupar a pagina em vez de tamanho fixo. */
  const [area, setArea] = useState({ width: 0, height: 0 });

  /**
   * Deslocamento de cada no arrastado pela pessoa.
   *
   * Fica separado da posicao calculada: trocar de visao, filtrar ou recarregar
   * recalcula o layout, e o que a pessoa moveu continua onde ela pos.
   */
  const [dragged, setDragged] = useState<Record<string, { dx: number; dy: number }>>({});

  const selected = selectedPartnerId;

  const [accounts, setAccounts] = useState<ScopeAccount[]>([]);
  const [edges, setEdges] = useState<BindEdge[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);

      const [accountList, edgeList] = await Promise.all([
        getHierarchyAccounts(tenantId),
        getBindEdges(tenantId),
      ]);

      setAccounts(accountList || []);
      setEdges(edgeList || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Parceiro pendente é o outro lado do convite, seja ele recebido ou enviado:
   * as duas pontas de um convite aberto aparecem no mapa como intenção.
   */
  const pendingPartners = useMemo(
    () =>
      [
        ...(receivedInvites || []).map((invite) => ({
          tenantId: invite.tenant_source_id,
          name: invite.source_tenant_name || TF('unnamed tenant'),
          // Convite recebido e unilateral vai me fazer receber: o tipo gravado no convite é
          // relativo a quem gerou, e aqui a perspectiva é a de quem olha.
          contractType: directionOfInvite(invite.contract_type, 'RECEIVED'),
        })),
        // Em convite enviado a contraparte é o destinatário, e o source sou eu:
        // usar o source_tenant_name aqui mostraria o nome de quem está olhando.
        ...(sentInvites || []).map((invite) => ({
          tenantId: invite.tenant_target_id,
          name: invite.target_tenant_name || TF('unnamed tenant'),
          // O espelho: convite que eu enviei e unilateral vai me fazer compartilhar.
          contractType: directionOfInvite(invite.contract_type, 'SENT'),
        })),
      ].filter((partner) => partner.tenantId !== tenantId),
    [receivedInvites, sentInvites, tenantId],
  );

  const partners: MapPartner[] = useMemo(
    () => partnersOf(connections, pendingPartners),
    [connections, pendingPartners],
  );

  /**
   * Alias por conta, para montar a URL do logo. Fica aqui e nao no mapLayout: o
   * layout calcula geometria, e alias e assunto de apresentacao.
   */
  const aliasById = useMemo(() => {
    const index = new Map<string, string>();

    (connections || []).forEach((connection) => {
      if (connection.partner_alias) {
        index.set(connection.partner_tenant_id, connection.partner_alias);
      }
    });

    (receivedInvites || []).forEach((invite) => {
      if (invite.source_tenant_alias) {
        index.set(invite.tenant_source_id, invite.source_tenant_alias);
      }
    });

    (sentInvites || []).forEach((invite) => {
      if (invite.target_tenant_alias) {
        index.set(invite.tenant_target_id, invite.target_tenant_alias);
      }
    });

    return index;
  }, [connections, receivedInvites, sentInvites]);

  /**
   * Mede a area do desenho com ResizeObserver, e nao uma vez na montagem.
   *
   * Na montagem o Canvas ainda nao existe: a tela esta em loading e o ref e nulo,
   * entao a area ficava zero e o mapa caia na largura minima em vez de ocupar a
   * pagina. O observer comeca a valer quando o elemento aparece, e tambem cobre
   * janela redimensionada e menu lateral abrindo.
   */
  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    const measure = () => {
      const box = canvas.getBoundingClientRect();

      setArea({ width: Math.floor(box.width), height: Math.floor(box.height) });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);

      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);

    observer.observe(canvas);

    return () => observer.disconnect();
    // O canvas so existe depois de carregar, entao o efeito precisa rodar de novo
    // quando o loading termina.
  }, [loading, loadError]);

  useEffect(() => {
    let alive = true;

    Array.from(new Set(aliasById.values())).forEach((alias) => {
      const probe = new Image();

      probe.onload = () => {
        if (alive) {
          setLogoReady((ready) => (ready.includes(alias) ? ready : [...ready, alias]));
        }
      };
      probe.src = partnerLogoUrl(alias);
    });

    // Desmontar durante o carregamento nao pode virar setState em componente morto.
    return () => {
      alive = false;
    };
  }, [aliasById]);

  /** Alias do no, se o logo dele existe. */
  const logoAliasOf = (nodeId: string): string | null => {
    const alias = aliasById.get(nodeId);

    return alias && logoReady.includes(alias) ? alias : null;
  };

  const layoutInput = useMemo(
    () => ({
      accounts,
      partners,
      edges,
      selectedPartnerId: selected,
      accountLabel: (count: number) => `${count} ${TF('stations')}`,
      rootTag: TF('head account'),
      accountTag: TF('subaccount'),
      width: area.width,
      height: area.height,
    }),
    [accounts, partners, edges, selected, area.width, area.height],
  );

  const base: MapLayout = useMemo(
    () => (view === 'flow' ? buildFlowLayout(layoutInput) : buildMentalLayout(layoutInput)),
    [layoutInput, view],
  );

  /**
   * O desenho final: posicao calculada mais o que a pessoa arrastou, e as ligacoes
   * recalculadas a partir disso.
   *
   * As ligacoes nao podem vir prontas do layout: arrastar um no move as duas pontas
   * das curvas que chegam nele, e uma linha parada no lugar antigo seria pior que
   * nao poder arrastar.
   */
  const layout: MapLayout = useMemo(() => {
    const nodes = base.nodes.map((node) => {
      const offset = dragged[node.id];

      return offset ? { ...node, x: node.x + offset.dx, y: node.y + offset.dy } : node;
    });

    const boxes = new Map<string, MapBox>(
      nodes.map((node) => [
        node.id,
        { x: node.x, y: node.y, width: node.width, height: node.height },
      ]),
    );

    // As duas familias de ligacao entram aqui: sem a dependencia, arrastar a matriz
    // apagaria a hierarquia do desenho.
    const links = [
      ...dependencyLinks(accounts, (id) => boxes.get(id)),
      ...buildLinks(layoutInput, (id) => boxes.get(id), rootIdOf(accounts)),
    ];

    // A area cresce se alguem arrastou um no para fora dela.
    const width = Math.max(base.width, ...nodes.map((n) => n.x + n.width + 16));
    const height = Math.max(base.height, ...nodes.map((n) => n.y + n.height + 16));

    return { width, height, nodes, links };
  }, [base, dragged, layoutInput, accounts]);

  /**
   * Arraste de um no.
   *
   * Ouve no window e nao no proprio no: o ponteiro sai da caixa durante o
   * movimento, e o listener no elemento perderia o evento no meio do gesto.
   */
  const startDrag = (node: MapNode, event: React.MouseEvent) => {
    if (!node.draggable) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const origin = { x: event.clientX, y: event.clientY };
    const start = dragged[node.id] || { dx: 0, dy: 0 };

    // Congela o deslocamento no inicio do gesto: somar sobre o estado corrente a
    // cada mousemove multiplicaria o movimento.
    draggedRef.current = { ...dragged };
    let moved = false;

    const onMove = (move: MouseEvent) => {
      const dx = move.clientX - origin.x;
      const dy = move.clientY - origin.y;

      // Alguns pixels de tolerancia: sem isso um clique tremido viraria arraste e a
      // selecao do parceiro nunca aconteceria.
      if (!moved && Math.abs(dx) + Math.abs(dy) < 4) {
        return;
      }

      moved = true;
      setDragged((current) => {
        const next = { ...current, [node.id]: { dx: start.dx + dx, dy: start.dy + dy } };

        // Mover a raiz leva as subcontas: elas sao fixas em relacao a ela, nao a
        // tela. Sem isto a hierarquia se desfaria no primeiro arraste.
        //
        // A ancora e SEMPRE o deslocamento congelado no inicio do gesto, e nunca o
        // estado corrente: o corrente ja contem o movimento dos mousemove
        // anteriores, e somar sobre ele fazia a subconta se distanciar mais a cada
        // passo em vez de acompanhar.
        if (node.kind === 'root') {
          accounts
            .filter((account) => account.tenant_id !== node.id)
            .forEach((account) => {
              const anchor = draggedRef.current[account.tenant_id] || { dx: 0, dy: 0 };

              next[account.tenant_id] = { dx: anchor.dx + dx, dy: anchor.dy + dy };
            });
        }

        return next;
      });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      // Clique sem arraste segue valendo como selecao do parceiro.
      if (!moved && node.clickable) {
        onSelectPartner(selected === node.id ? null : node.id);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const tagOf = (node: MapNode): string => {
    if (node.kind !== 'partner') return node.tag;

    const style = STATUS_STYLE[node.tag as keyof typeof STATUS_STYLE];

    return style ? TF(style.label) : node.tag;
  };

  const hint =
    view === 'mental'
      ? T(
          'Mind map: your head account at the center, subaccounts on the left, partners around. Solid arrow = I share; dashed arrow back = I receive.',
        )
      : selected
        ? T('Showing only the selected partner links — click it again to see all.')
        : T('Click a partner on the right to isolate its links.');

  if (loading) {
    return <ListSkeleton />;
  }

  if (loadError) {
    return (
      <Result
        status="warning"
        title={T('Could not load the map.')}
        extra={
          <Button type="primary" onClick={load}>
            {T('Try again')}
          </Button>
        }
      />
    );
  }

  if (!partners.length) {
    return <Empty>{T('No contracts or open invites to draw yet.')}</Empty>;
  }

  return (
    <div>
      <Head>
        <div>
          <Title>{T('My hierarchy → partners')}</Title>
          <Hint>{hint}</Hint>
        </div>

        <ViewSwitch>
          <ViewButton type="button" active={view === 'flow'} onClick={() => setView('flow')}>
            {T('Flow')}
          </ViewButton>
          <ViewButton type="button" active={view === 'mental'} onClick={() => setView('mental')}>
            {T('Mind map')}
          </ViewButton>
        </ViewSwitch>
      </Head>

      {/*
        O ref precisa estar AQUI: sem ele o ResizeObserver nunca achava o elemento, a area
        media zero e o desenho caia no tamanho padrao do layout (820x460) — maior que a caixa
        em tela estreita, o que era a barra de rolagem que aparecia ao abrir.
      */}
      <Canvas ref={canvasRef}>
        {/*
          viewBox com 100% de largura e altura: o desenho se ajusta a caixa em vez de
          transbordar. O layout ja tenta caber, mas ele cresce quando ha mais parceiros do que
          linhas disponiveis — e transbordar virava barra de rolagem na abertura.

          preserveAspectRatio "meet" encolhe mantendo a proporcao; nada de esticar rosto de
          ninguem. Quem precisa de mais espaco arrasta a borda de baixo da caixa, e o desenho
          cresce de volta.
        */}
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={T('My hierarchy → partners')}
          style={{ display: 'block' }}
        >
          {/*
            Um marker por cor: o SVG nao herda o stroke no marcador, entao sem
            preenchimento proprio a ponta da seta sairia preta em toda ligacao.
          */}
          <defs>
            {LINK_MARKERS.map((marker) => (
              <marker
                key={marker.id}
                id={marker.id}
                viewBox="0 0 8 8"
                refX={7}
                refY={4}
                markerWidth={5}
                markerHeight={5}
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 z" fill={marker.color} />
              </marker>
            ))}
          </defs>

          {/*
            As ligações vêm antes dos nós no documento para ficarem por baixo:
            SVG não tem z-index, a ordem de pintura é a ordem do documento.
          */}
          {layout.links.map((link) =>
            link.kind === 'dependency' ? (
              <path
                key={link.id}
                d={link.d}
                fill="none"
                stroke={link.color}
                strokeWidth={1.2}
                // Sem seta e sem cor de contrato: nao ha nada negociado entre a
                // matriz e a subconta dela, e a mesma ponta faria a hierarquia
                // parecer um acordo.
              />
            ) : (
              <g key={link.id}>
                {/*
                  Trilho invisivel e largo por cima da curva: a linha tem menos de
                  dois pixels, e acertar isso com o ponteiro seria sorte.
                */}
                <path
                  d={link.d}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={14}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onEditContract(link.partnerTenantId)}
                >
                  <title>{T('Open contract')}</title>
                </path>
                <path
                  d={link.d}
                  fill="none"
                  stroke={link.color}
                  strokeWidth={link.dimmed ? 1 : 1.8}
                  strokeDasharray={link.dashed ? '5 4' : undefined}
                  markerEnd={`url(#${link.markerId})`}
                  pointerEvents="none"
                />
              </g>
            ),
          )}

          {view === 'flow' && (
            <>
              <text x={16} y={24} fontSize={10} fill="#8a9099" letterSpacing="1">
                {T('MY ACCOUNTS')}
              </text>
              <text
                x={layout.width - 16}
                y={24}
                fontSize={10}
                fill="#8a9099"
                letterSpacing="1"
                textAnchor="end"
              >
                {T('PARTNERS')}
              </text>
            </>
          )}

          {layout.nodes.map((node) => (
            <g
              key={node.id}
              onMouseDown={(event) => startDrag(node, event)}
              style={{ cursor: node.draggable ? 'grab' : 'default' }}
            >
              <NodeRect
                x={node.x}
                y={node.y}
                width={node.width}
                height={node.height}
                rx={7}
                root={node.kind === 'root'}
                dimmed={node.dimmed}
              />
              {/*
                O logo entra apenas nos nos de parceiro: as contas da propria
                hierarquia sao a mesma organizacao de quem olha, e repetir o
                mesmo logo em cada uma nao informa nada.
              */}
              {node.kind === 'partner' && logoAliasOf(node.id) && (
                <image
                  href={partnerLogoUrl(logoAliasOf(node.id) as string)}
                  x={node.x + 10}
                  y={node.y + (node.height - 20) / 2}
                  width={20}
                  height={20}
                  preserveAspectRatio="xMidYMid meet"
                />
              )}

              <NodeTitle
                x={node.x + (node.kind === 'partner' && logoAliasOf(node.id) ? 38 : 12)}
                y={node.y + 19}
                root={node.kind === 'root'}
                dimmed={node.dimmed}
              >
                {node.title}
              </NodeTitle>
              <NodeSubtitle
                x={node.x + (node.kind === 'partner' && logoAliasOf(node.id) ? 38 : 12)}
                y={node.y + 34}
                root={node.kind === 'root'}
                dimmed={node.dimmed}
              >
                {node.subtitle} · {tagOf(node)}
              </NodeSubtitle>
            </g>
          ))}
        </svg>
      </Canvas>

      <Legend>
        <LegendItem>
          <svg width={30} height={10}>
            <line x1={0} y1={5} x2={22} y2={5} stroke="#2f6fe0" strokeWidth={2} />
            <path d="M22,1 L30,5 L22,9 z" fill="#2f6fe0" />
          </svg>
          {T('Active bidirectional contract — both apps display')}
        </LegendItem>
        <LegendItem>
          <svg width={30} height={10}>
            <line x1={0} y1={5} x2={22} y2={5} stroke="#9aa3af" strokeWidth={2} />
            <path d="M22,1 L30,5 L22,9 z" fill="#9aa3af" />
          </svg>
          {T('Active one-way contract — only the partner displays')}
        </LegendItem>
        <LegendItem>
          <svg width={30} height={10}>
            <line x1={0} y1={5} x2={22} y2={5} stroke="#e0b25e" strokeWidth={2} strokeDasharray="5 4" />
            <path d="M22,1 L30,5 L22,9 z" fill="#e0b25e" />
          </svg>
          {T('Pending invite — nothing shared yet')}
        </LegendItem>
        <LegendItem>
          <svg width={30} height={10}>
            <line x1={0} y1={5} x2={22} y2={5} stroke="#e0700f" strokeWidth={2} strokeDasharray="5 4" />
            <path d="M22,1 L30,5 L22,9 z" fill="#e0700f" />
          </svg>
          {T('Suspended — contract stands, nothing displayed')}
        </LegendItem>
      </Legend>
    </div>
  );
};
